import { useEffect, useRef, useState } from 'react';

// Modal that opens the user's webcam via getUserMedia, shows a live preview,
// and lets them snap a frame that's returned as a JPEG File via onCapture.
//
// On mobile we never reach this — PublicUploadPortal uses the native
// <input capture> path which opens the OS camera UI. This is the desktop
// fallback (browsers ignore `capture` on desktop, falling back to file picker).
export function CameraCapture({ open, onCapture, onClose, isEs }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError('');
    setReady(false);

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(isEs ? 'Tu navegador no soporta acceso a la cámara' : 'Your browser does not support camera access');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        const name = e?.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setError(isEs ? 'Permiso denegado. Permite el acceso a la cámara y vuelve a intentar.' : 'Permission denied. Allow camera access and try again.');
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setError(isEs ? 'No se encontró ninguna cámara conectada.' : 'No camera found on this device.');
        } else {
          setError(e?.message || (isEs ? 'No se pudo abrir la cámara.' : 'Failed to open camera.'));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, isEs]);

  const handleSnap = () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
      handleClose();
    }, 'image/jpeg', 0.92);
  };

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setReady(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      role="dialog"
      aria-modal="true"
    >
      <div style={{ width: '100%', maxWidth: 720, background: '#0f172a', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '.95rem' }}>
            📷 {isEs ? 'Tomar Foto' : 'Take Photo'}
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer', padding: '.25rem .5rem', lineHeight: 1 }}
            aria-label={isEs ? 'Cerrar' : 'Close'}
          >
            ×
          </button>
        </div>

        <div style={{ position: 'relative', background: '#000', aspectRatio: '4/3' }}>
          {error ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fca5a5', padding: '2rem', textAlign: 'center', fontSize: '.9rem' }}>
              ⚠️ {error}
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
          {!error && !ready && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '.9rem', background: 'rgba(0,0,0,0.4)' }}>
              {isEs ? 'Iniciando cámara…' : 'Starting camera…'}
            </div>
          )}
        </div>

        <div style={{ padding: '1rem 1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', background: '#0f172a' }}>
          <button
            onClick={handleClose}
            style={{ padding: '.85rem', background: '#1e293b', color: '#cbd5e1', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.9rem' }}
          >
            {isEs ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            onClick={handleSnap}
            disabled={!ready || !!error}
            style={{ padding: '.85rem', background: ready && !error ? '#0ea5e9' : '#475569', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: ready && !error ? 'pointer' : 'not-allowed', fontSize: '.9rem' }}
          >
            📸 {isEs ? 'Capturar' : 'Capture'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Detects whether the device is a phone or tablet running a mobile OS.
// Used so we keep the native <input capture> on phones (which opens the OS
// camera UI directly — better UX than getUserMedia on mobile). Strict UA
// match only — Windows touch laptops should still get the desktop modal.
export function isMobileDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /android|iphone|ipad|ipod|opera mini|iemobile|mobile safari/i.test(ua);
}
